'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { LuArrowUpLeft, LuArrowUpRight } from 'react-icons/lu'
import { toast } from 'sonner'

import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'
import {
  TurnstileWidget,
  type TurnstileWidgetHandle,
} from '@/components/shared/TurnstileWidget'
import {
  getStoredCountryName,
  getStoredNameParts,
  getStoredPhoneNumber,
  getStoredProfile,
} from '@/lib/profile'
import {
  isValidContactCompany,
  isValidContactEmail,
  isValidContactMessage,
  sanitizeContactCompanyInput,
} from '@/lib/utils'

export type StockRequestLine = {
  productCode: string
  partNumber?: string
  productName?: string
  requestedQty: number
  availableQty?: number
  shortfallQty?: number
}

export type StockRequestFormData = {
  requestedQty: number
  message: string
  email: string
  company: string
  country: string
  phoneNumber: string
  firstName: string
  lastName: string
  lines: StockRequestLine[]
}

export type RequestForStockModalProps = {
  isOpen: boolean
  onClose: () => void
  lines: StockRequestLine[]
  locale: AppLocale
  submitMode?: 'standalone' | 'combined'
  onCombinedSubmit?: (formData: StockRequestFormData, turnstileToken: string) => Promise<string | void>
  onSuccess?: () => void
}

const MIN_MESSAGE_LENGTH = 10
const MIN_COMPANY_LENGTH = 2
const MAX_COMPANY_LENGTH = 80

const MODAL_TEXT: Record<
  AppLocale,
  {
    title: string
    qty: string
    message: string
    email: string
    company: string
    submit: string
    close: string
    productSummary: string
    available: string
    requested: string
    shortfall: string
    submitting: string
    submitted: string
    failed: string
    qtyRequired: string
    messageRequired: string
    messageMinLength: string
    emailRequired: string
    emailInvalid: string
    companyRequired: string
    companyInvalid: string
    turnstileRequired: string
    defaultMessageSingle: (code: string, productName?: string) => string
    defaultMessageMultiple: (codes: string) => string
  }
> = {
  en: {
    title: 'Submit Enquiry',
    qty: 'Quantity',
    message: 'Message',
    email: 'Email',
    company: 'Company Name',
    submit: 'Submit Enquiry',
    close: 'Close',
    productSummary: 'Products',
    available: 'Available',
    requested: 'Requested',
    shortfall: 'Shortfall',
    submitting: 'Submitting enquiry…',
    submitted: 'Enquiry submitted successfully.',
    failed: 'Failed to submit enquiry.',
    qtyRequired: 'Quantity is required.',
    messageRequired: 'Message is required.',
    messageMinLength: 'Message must be at least 10 characters.',
    emailRequired: 'Email is required.',
    emailInvalid: 'Please enter a valid email.',
    companyRequired: 'Company name is required.',
    companyInvalid: 'Company name is invalid.',
    turnstileRequired: 'Please complete the security verification before submitting.',
    defaultMessageSingle: (code, productName) =>
      `Requesting stock for ${code}${productName ? ` - ${productName}` : ''}.`,
    defaultMessageMultiple: (codes) => `Requesting stock for the following products: ${codes}.`,
  },
  fr: {
    title: 'Envoyer la demande',
    qty: 'Quantité',
    message: 'Message',
    email: 'E-mail',
    company: 'Nom de l’entreprise',
    submit: 'Envoyer la demande',
    close: 'Fermer',
    productSummary: 'Produits',
    available: 'Disponible',
    requested: 'Demandé',
    shortfall: 'Manquant',
    submitting: 'Envoi de la demande…',
    submitted: 'Demande envoyée avec succès.',
    failed: 'Échec de l’envoi de la demande.',
    qtyRequired: 'La quantité est requise.',
    messageRequired: 'Le message est requis.',
    messageMinLength: 'Le message doit contenir au moins 10 caractères.',
    emailRequired: 'L’e-mail est requis.',
    emailInvalid: 'Veuillez saisir un e-mail valide.',
    companyRequired: 'Le nom de l’entreprise est requis.',
    companyInvalid: 'Le nom de l’entreprise est invalide.',
    turnstileRequired:
      'Veuillez compléter la vérification de sécurité avant de soumettre.',
    defaultMessageSingle: (code, productName) =>
      `Demande de stock pour ${code}${productName ? ` - ${productName}` : ''}.`,
    defaultMessageMultiple: (codes) => `Demande de stock pour les produits suivants : ${codes}.`,
  },
  ar: {
    title: 'إرسال الطلب',
    qty: 'الكمية',
    message: 'الرسالة',
    email: 'البريد الإلكتروني',
    company: 'اسم الشركة',
    submit: 'إرسال الطلب',
    close: 'إغلاق',
    productSummary: 'المنتجات',
    available: 'متوفر',
    requested: 'مطلوب',
    shortfall: 'النقص',
    submitting: 'جارٍ إرسال الطلب…',
    submitted: 'تم إرسال الاستفسار بنجاح.',
    failed: 'فشل إرسال الاستفسار.',
    qtyRequired: 'الكمية مطلوبة.',
    messageRequired: 'الرسالة مطلوبة.',
    messageMinLength: 'يجب أن تحتوي الرسالة على 10 أحرف على الأقل.',
    emailRequired: 'البريد الإلكتروني مطلوب.',
    emailInvalid: 'يرجى إدخال بريد إلكتروني صحيح.',
    companyRequired: 'اسم الشركة مطلوب.',
    companyInvalid: 'اسم الشركة غير صالح.',
    turnstileRequired: 'يرجى إكمال التحقق الأمني قبل الإرسال.',
    defaultMessageSingle: (code, productName) =>
      `طلب مخزون لـ ${code}${productName ? ` - ${productName}` : ''}.`,
    defaultMessageMultiple: (codes) => `طلب مخزون للمنتجات التالية: ${codes}.`,
  },
}

function getLinePartReference(line: StockRequestLine): string {
  return line.partNumber?.trim() || line.productCode
}

function buildDefaultMessage(lines: StockRequestLine[], t: (typeof MODAL_TEXT)[AppLocale]): string {
  if (lines.length === 1) {
    const line = lines[0]!
    return t.defaultMessageSingle(getLinePartReference(line), line.productName)
  }
  const partNumbers = lines.map((line) => getLinePartReference(line)).join(', ')
  return t.defaultMessageMultiple(partNumbers)
}

export function RequestForStockModal({
  isOpen,
  onClose,
  lines,
  locale,
  submitMode = 'standalone',
  onCombinedSubmit,
  onSuccess,
}: RequestForStockModalProps) {
  const t = MODAL_TEXT[locale] ?? MODAL_TEXT.en
  const [mounted, setMounted] = useState(false)
  const dialogRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const isMultiLine = lines.length > 1
  const primaryLine = lines[0]

  const [requestedQty, setRequestedQty] = useState('1')
  const [message, setMessage] = useState('')
  const [email, setEmail] = useState('')
  const [company, setCompany] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const turnstileRef = useRef<TurnstileWidgetHandle>(null)

  useEffect(() => {
    if (!isOpen) return

    const profile = getStoredProfile()
    const defaultQty = primaryLine?.shortfallQty ?? primaryLine?.requestedQty ?? 1

    setRequestedQty(String(Math.max(1, defaultQty)))
    setMessage(buildDefaultMessage(lines, t))
    setEmail(profile.email || '')
    setCompany(profile.company || '')
    setFieldErrors({})
    setSubmitting(false)
    setTurnstileToken(null)
    turnstileRef.current?.reset()

    closeRef.current?.focus()
  }, [isOpen, lines, primaryLine?.requestedQty, primaryLine?.shortfallQty, t])

  useEffect(() => {
    setMounted(true)
  }, [])

  useBodyScrollLock(isOpen)

  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }

    window.addEventListener('keydown', handleEscape)

    return () => {
      window.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !dialogRef.current) return
    const focusables = dialogRef.current.querySelectorAll<HTMLElement>(
      'button, [href], input, textarea, [tabindex]:not([tabindex="-1"])',
    )
    const list = Array.from(focusables)
    const first = list[0]
    const last = list[list.length - 1]

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last?.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first?.focus()
    }
  }, [])

  const validate = (): boolean => {
    const errors: Record<string, string> = {}

    if (!isMultiLine) {
      const qty = parseInt(requestedQty, 10)
      if (!requestedQty || !Number.isInteger(qty) || qty < 1 || qty > 999) {
        errors.requestedQty = t.qtyRequired
      }
    }

    const trimmedMessage = message.trim()
    if (!trimmedMessage) {
      errors.message = t.messageRequired
    } else if (
      trimmedMessage.length < MIN_MESSAGE_LENGTH ||
      !isValidContactMessage(trimmedMessage)
    ) {
      errors.message = t.messageMinLength
    }

    const trimmedEmail = email.trim()
    if (!trimmedEmail) {
      errors.email = t.emailRequired
    } else if (!isValidContactEmail(trimmedEmail)) {
      errors.email = t.emailInvalid
    }

    const trimmedCompany = company.trim()
    if (!trimmedCompany || trimmedCompany.length < MIN_COMPANY_LENGTH) {
      errors.company = t.companyRequired
    } else if (
      trimmedCompany.length > MAX_COMPANY_LENGTH ||
      !isValidContactCompany(trimmedCompany)
    ) {
      errors.company = t.companyInvalid
    }

    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  const buildFormData = (): StockRequestFormData => {
    const parsedQty = parseInt(requestedQty, 10)
    const { firstName, lastName } = getStoredNameParts()
    const resolvedLines = isMultiLine
      ? lines.map((line) => ({
          ...line,
          requestedQty: line.shortfallQty ?? line.requestedQty,
        }))
      : [
          {
            productCode: primaryLine!.productCode,
            partNumber: primaryLine?.partNumber,
            productName: primaryLine?.productName,
            requestedQty: parsedQty,
            availableQty: primaryLine?.availableQty,
            shortfallQty: parsedQty,
          },
        ]

    return {
      requestedQty: isMultiLine
        ? resolvedLines.reduce((sum, line) => sum + line.requestedQty, 0)
        : parsedQty,
      message: message.trim(),
      email: email.trim().toLowerCase(),
      company: company.trim(),
      country: getStoredCountryName(),
      phoneNumber: getStoredPhoneNumber(),
      firstName,
      lastName,
      lines: resolvedLines,
    }
  }

  const handleQtyChange = (value: string) => {
    setRequestedQty(value.replace(/\D/g, '').slice(0, 3))
  }

  const handleQtyBlur = () => {
    const qty = parseInt(requestedQty, 10)
    if (!requestedQty || !Number.isFinite(qty) || qty < 1) {
      setRequestedQty('1')
      return
    }
    if (qty > 999) {
      setRequestedQty('999')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (submitting || !primaryLine) return
    if (!validate()) return

    if (!turnstileToken) {
      toast.error(t.turnstileRequired)
      return
    }

    const formData = buildFormData()

    if (submitMode === 'combined' && onCombinedSubmit) {
      setSubmitting(true)
      try {
        await toast.promise(onCombinedSubmit(formData, turnstileToken), {
          loading: t.submitting,
          success: (message) =>
            typeof message === 'string' && message.length > 0 ? message : t.submitted,
          error: (err) => (err instanceof Error ? err.message : t.failed),
        })
        onSuccess?.()
        onClose()
      } catch {
        setTurnstileToken(null)
        turnstileRef.current?.reset()
      } finally {
        setSubmitting(false)
      }
      return
    }

    setSubmitting(true)
    toast.promise(
      (async () => {
        const res = await fetch('/api/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            type: 'stock_request',
            lines: formData.lines.map((line) => ({
              productCode: line.productCode,
              partNumber: line.partNumber,
              productName: line.productName,
              requestedQty: line.requestedQty,
              availableQty: line.availableQty,
              shortfallQty: line.shortfallQty ?? line.requestedQty,
            })),
            message: formData.message,
            email: formData.email,
            company: formData.company,
            country: formData.country,
            contactNumber: formData.phoneNumber,
            firstName: formData.firstName,
            lastName: formData.lastName,
            turnstileToken,
          }),
        })

        const data = await res.json().catch(() => ({}))
        if (!res.ok || !data.success) {
          setTurnstileToken(null)
          turnstileRef.current?.reset()
          const errorMessage =
            typeof data.error === 'string'
              ? data.error
              : typeof data.message === 'string'
                ? data.message
                : t.failed
          throw new Error(errorMessage)
        }

        onSuccess?.()
        onClose()
      })(),
      {
        loading: t.submitting,
        success: t.submitted,
        error: (err) => (err instanceof Error ? err.message : t.failed),
        finally: () => setSubmitting(false),
      },
    )
  }

  if (!isOpen || !primaryLine) return null

  const inputClass =
    'w-full min-h-[44px] rounded-none border border-neutral-200 bg-neutral-100 px-3 py-2.5 font-poppins text-sm leading-normal text-[#1a1a1a] placeholder:text-neutral-500 focus:border-[#FF0000] focus:outline-none focus:ring-2 focus:ring-[#FF0000] focus:ring-offset-0 disabled:opacity-60'
  const labelClass =
    'font-poppins mb-2 block text-sm font-semibold uppercase leading-normal text-[#545454]'
  const isRtl = locale === 'ar'

  const modal = (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={t.title}
      className="fixed inset-0 z-[1000] flex items-center justify-center overflow-hidden bg-black/40 p-4 sm:p-6"
      onKeyDown={handleKeyDown}
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      <button
        type="button"
        aria-label={t.close}
        className="absolute inset-0 cursor-default"
        onClick={onClose}
        tabIndex={-1}
      />

      <div className="relative z-10 w-full max-w-lg rounded-md bg-white p-6 shadow-lg sm:p-8 max-h-[96vh] overflow-y-auto">
        <div className="mb-6 flex items-start justify-between gap-4">
          <h2 className="font-bebas-neue text-2xl uppercase leading-tight tracking-[1px] text-[#111] sm:text-3xl">
            {t.title}
          </h2>
          <button
            ref={closeRef}
            type="button"
            aria-label={t.close}
            onClick={onClose}
            className="-m-1 shrink-0 rounded p-1 text-neutral-500 transition-colors hover:text-[#111] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF0000] focus-visible:ring-offset-2"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {isMultiLine ? (
          <div className="mb-6 rounded border border-neutral-200 bg-neutral-50 p-4">
            <p className="font-poppins mb-3 text-xs font-semibold uppercase text-neutral-600">
              {t.productSummary}
            </p>
            <ul className="space-y-3">
              {lines.map((line) => (
                <li
                  key={line.productCode}
                  className="font-poppins text-sm leading-normal text-neutral-700"
                >
                  <span className="font-semibold text-[#111]">{getLinePartReference(line)}</span>
                  {line.productName ? ` — ${line.productName}` : ''}
                  <span className="mt-1 block text-xs text-neutral-500">
                    {t.available}: {line.availableQty ?? 0} · {t.shortfall}:{' '}
                    {line.shortfallQty ?? line.requestedQty}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5 sm:gap-6">
          {!isMultiLine ? (
            <div className="space-y-2">
              <label htmlFor="stock-request-qty" className={labelClass}>
                {t.qty}
              </label>
              <input
                id="stock-request-qty"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                value={requestedQty}
                onChange={(e) => handleQtyChange(e.target.value)}
                onBlur={handleQtyBlur}
                className={inputClass}
                disabled={submitting}
              />
              {fieldErrors.requestedQty ? (
                <p className="font-poppins mt-1.5 text-xs text-[#FF0000]">
                  {fieldErrors.requestedQty}
                </p>
              ) : null}
            </div>
          ) : null}

          <div className="space-y-2">
            <label htmlFor="stock-request-message" className={labelClass}>
              {t.message}
            </label>
            <textarea
              id="stock-request-message"
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className={`${inputClass} min-h-[120px] resize-none py-3`}
              disabled={submitting}
            />
            {fieldErrors.message ? (
              <p className="font-poppins mt-1.5 text-xs text-[#FF0000]">{fieldErrors.message}</p>
            ) : null}
          </div>

          <div className="space-y-2">
            <label htmlFor="stock-request-email" className={labelClass}>
              {t.email}
            </label>
            <input
              id="stock-request-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClass}
              disabled={submitting}
            />
            {fieldErrors.email ? (
              <p className="font-poppins mt-1.5 text-xs text-[#FF0000]">{fieldErrors.email}</p>
            ) : null}
          </div>

          <div className="space-y-2">
            <label htmlFor="stock-request-company" className={labelClass}>
              {t.company}
            </label>
            <input
              id="stock-request-company"
              type="text"
              value={company}
              onChange={(e) => setCompany(sanitizeContactCompanyInput(e.target.value))}
              className={inputClass}
              disabled={submitting}
            />
            {fieldErrors.company ? (
              <p className="font-poppins mt-1.5 text-xs text-[#FF0000]">{fieldErrors.company}</p>
            ) : null}
          </div>

          <TurnstileWidget
            ref={turnstileRef}
            active={isOpen}
            action="stock_request"
            onVerify={setTurnstileToken}
            onExpire={() => setTurnstileToken(null)}
          />

          <div className="login-submit-btn-wrapper inline-flex w-full pt-1">
            <button
              type="submit"
              disabled={submitting}
              className="login-submit-btn flex min-h-[44px] w-full items-center justify-center gap-2 rounded-none bg-[#E40520] px-6 py-3 font-poppins text-sm font-normal uppercase leading-snug tracking-normal text-white focus:outline-none focus:ring-2 focus:ring-[#E40520] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 sm:min-h-[48px] sm:py-4"
            >
              {submitting ? t.submitting : t.submit}
              {isRtl ? (
                <LuArrowUpLeft className="login-submit-btn__arrow h-5 w-5 shrink-0" aria-hidden />
              ) : (
                <LuArrowUpRight className="login-submit-btn__arrow h-5 w-5 shrink-0" aria-hidden />
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )

  if (mounted && typeof document !== 'undefined') {
    return createPortal(modal, document.body)
  }

  return modal
}
